/**
 * Measura quick-add for collection cards
 * ----------------------------------------
 * For products tagged as weighted (Measura's product filter tag),
 * the grid item form renders an extra weight input. This script:
 *   1. Intercepts the add-to-cart button click (capture phase) on those cards
 *   2. Reads the weight (in grams)
 *   3. Calls window.Measura.buildLineItemProperties to build canonical
 *      Measura line item properties
 *   4. POSTs to /cart/add.js with those properties attached
 *   5. Navigates to /cart on success (prototype UX — drawer integration
 *      can come later if the client signs off on this flow)
 *
 * Non-weighted product cards are untouched.
 *
 * window.Measura is loaded by the Measura app embed (configured in the
 * theme editor), so it should be available on every storefront page.
 * We guard for absence anyway.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = 'form[data-measura-form="true"]';
  var WEIGHT_INPUT_SELECTOR = '[data-measura-weight-input]';
  var BUTTON_SELECTOR = '[data-add-to-cart]';
  // Set to true on a click we've already handled, so a programmatic re-click
  // (if we ever needed it) bypasses our interceptor.
  var IGNORE_FLAG = '__measuraHandled';

  function setButtonState(button, state, message) {
    if (!button) return;
    if (state === 'loading') {
      button.classList.add('is-loading');
      button.disabled = true;
    } else {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
    var errorEl = button.querySelector('[data-message-error]');
    if (errorEl) errorEl.textContent = message || '';
  }

  async function handleClick(event) {
    if (event[IGNORE_FLAG]) return;

    // Find the add-to-cart button up the path (handles clicks on inner <span>s).
    var button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    var form = button.closest(FORM_SELECTOR);
    if (!form) return; // Not a weighted product — let Broadcast handle it

    // Capture phase: stop everything else (Broadcast's own click handler
    // that dispatches `theme:cart:add`) from running.
    event.preventDefault();
    event.stopImmediatePropagation();

    var weightInput = form.querySelector(WEIGHT_INPUT_SELECTOR);
    var variantInput = form.querySelector('input[name="id"]');

    if (!weightInput || !variantInput) {
      console.error('[Measura] Missing weight or variant input on weighted form', form);
      return;
    }

    var grams = parseInt(weightInput.value, 10);
    var min = parseInt(weightInput.min, 10) || 0;
    if (!grams || grams < min) {
      setButtonState(button, 'idle', 'Enter at least ' + min + 'g');
      weightInput.focus();
      return;
    }

    if (!window.Measura || typeof window.Measura.buildLineItemProperties !== 'function') {
      console.error('[Measura] window.Measura runtime not available. Is the app embed enabled?');
      setButtonState(button, 'idle', 'Measura not loaded');
      return;
    }

    setButtonState(button, 'loading');

    try {
      var properties = await window.Measura.buildLineItemProperties({
        productId: form.dataset.productId,
        productHandle: form.dataset.productHandle,
        variantId: variantInput.value,
        measurement: {
          dimensions: [{ value: grams, unit: 'g' }]
        }
      });

      // Convert [{name, value}] array into the object shape /cart/add.js expects
      var propsObject = {};
      (properties || []).forEach(function (p) {
        propsObject[p.name] = p.value;
      });

      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantInput.value,
          quantity: 1,
          properties: propsObject
        })
      });

      if (!response.ok) {
        var errText = await response.text();
        throw new Error(errText || 'Cart add failed: ' + response.status);
      }

      // Prototype: always navigate to cart so the result is visible.
      // Replace with drawer refresh later if client approves the flow.
      window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    } catch (err) {
      console.error('[Measura] Add to cart failed', err);
      setButtonState(button, 'idle', 'Could not add — try again');
    }
  }

  // Capture phase so we run BEFORE Broadcast's own click handler
  // (which preventDefaults the click and dispatches `theme:cart:add`).
  document.addEventListener('click', handleClick, true);
})();
